import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ServiceIcon } from "@/lib/icons";
import { searchServices } from "@/lib/search";

export function IconPicker({ icons, value, onChange, testId }) {
  return (
    <details className="rounded-2xl border border-slate-200 p-2">
      <summary className="flex cursor-pointer items-center gap-2 text-base font-semibold"><ServiceIcon icon={value} /> Trocar ícone</summary>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testId}>
        {icons.map((i) => (
          <button key={i} type="button" title={i} onClick={() => onChange(i)} data-testid={`${testId}-${i}`}
            className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 ${value === i ? "border-orange-500 bg-orange-500 text-white" : "border-slate-200 text-slate-700"}`}>
            <ServiceIcon icon={i} className="h-5 w-5" />
          </button>
        ))}
      </div>
    </details>
  );
}

export function GroupInput({ groups, value, onChange, testId }) {
  return (
    <>
      <input list="po-groups" className="po-input min-h-[48px]" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Grupo" data-testid={testId} />
      <datalist id="po-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
    </>
  );
}

function ServiceRow({ s, meta, reload }) {
  const [f, setF] = useState({ name: s.name, icon: s.icon, group: s.group || "", active: s.active });
  const save = async () => {
    try { await api.put(`/admin/services/${s.id}`, f); toast.success("Serviço salvo"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`po-card space-y-2 ${f.active ? "" : "opacity-60"}`} data-testid={`admin-service-${s.slug}`}>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-center">
        <input className="po-input min-h-[48px]" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`admin-service-name-${s.slug}`} />
        <GroupInput groups={meta.groups} value={f.group} onChange={(group) => setF({ ...f, group })} testId={`admin-service-group-${s.slug}`} />
        <label className="flex items-center gap-2 text-base font-semibold"><input type="checkbox" className="h-5 w-5 accent-orange-500" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid={`admin-service-active-${s.slug}`} /> Ativo</label>
        <button onClick={save} className="po-btn-dark min-h-[48px] text-base" data-testid={`admin-service-save-${s.slug}`}>Salvar</button>
      </div>
      <IconPicker icons={meta.icons} value={f.icon} onChange={(icon) => setF({ ...f, icon })} testId={`admin-service-icons-${s.slug}`} />
    </div>
  );
}

export default function AdminServices() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-services"], queryFn: () => api.get("/admin/services").then((r) => r.data) });
  const [nf, setNf] = useState({ name: "", icon: "wrench", group: "Serviços Gerais" });
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [limit, setLimit] = useState(30);
  const reload = () => { qc.invalidateQueries({ queryKey: ["admin-services"] }); qc.invalidateQueries({ queryKey: ["services"] }); };
  const list = useMemo(() => {
    const all = (data?.services || []).filter((s) => !group || s.group === group);
    return q ? searchServices(all, q) : all;
  }, [data, q, group]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/services", { ...nf, active: true }); setNf({ ...nf, name: "" }); toast.success("Serviço adicionado"); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  if (!data) return null;
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="po-card space-y-3 border-2 border-dashed border-orange-300" data-testid="admin-service-add-form">
        <p className="text-xl font-bold">Nova categoria</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <input className="po-input" placeholder="Nome (ex.: Instalador de rede de proteção)" required value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} data-testid="admin-service-new-name" />
          <GroupInput groups={data.groups} value={nf.group} onChange={(g) => setNf({ ...nf, group: g })} testId="admin-service-new-group" />
        </div>
        <IconPicker icons={data.icons} value={nf.icon} onChange={(icon) => setNf({ ...nf, icon })} testId="admin-service-new-icons" />
        <button className="po-btn-primary" data-testid="admin-service-add-button"><Plus className="h-5 w-5" /> Adicionar categoria</button>
      </form>
      <div className="grid gap-2 sm:grid-cols-2">
        <input className="po-input" placeholder="Buscar categoria" value={q} onChange={(e) => { setQ(e.target.value); setLimit(30); }} data-testid="admin-service-search" />
        <select className="po-select" value={group} onChange={(e) => { setGroup(e.target.value); setLimit(30); }} data-testid="admin-service-group-filter">
          <option value="">Todos os grupos ({data.services.length})</option>
          {data.groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      {list.slice(0, limit).map((s) => <ServiceRow key={s.id} s={s} meta={data} reload={reload} />)}
      {limit < list.length && <button onClick={() => setLimit(limit + 30)} className="po-btn-outline w-full" data-testid="admin-service-more">Mostrar mais ({list.length - limit})</button>}
    </div>
  );
}
